// modules/tenant-builder/steps/ReviewStep.tsx

import React from 'react';
import { Tenant } from '../../../core/types';
import { Building2, Globe, Palette, Settings, CheckCircle } from 'lucide-react';

interface Props {
  data: Partial<Tenant>;
}

export const ReviewStep: React.FC<Props> = ({ data }) => {
  const enabledModulesCount = data.enabledModules 
    ? Object.values(data.enabledModules).filter(Boolean).length 
    : 0;

  return (
    <div className="space-y-6">
      <div className="bg-gradient-to-r from-blue-50 to-cyan-50 border border-blue-200 rounded-xl p-6 mb-6">
        <h3 className="text-xl font-bold text-blue-900 mb-2 flex items-center gap-2">
          <CheckCircle className="text-green-500" />
          Tudo Pronto! Revise as Informações
        </h3>
        <p className="text-blue-700">
          Verifique os dados abaixo antes de criar seu site. Você poderá editar tudo depois no painel administrativo.
        </p>
      </div>

      <div className="space-y-4">
        {/* Informações do Negócio */}
        <div className="border border-gray-200 rounded-lg p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="bg-blue-100 p-2 rounded-lg">
              <Building2 className="text-blue-600" size={24} />
            </div>
            <h4 className="font-semibold text-lg">Informações do Negócio</h4>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <span className="text-sm text-gray-500">Nome:</span>
              <p className="font-medium">{data.businessName || '-'}</p>
            </div>
            <div>
              <span className="text-sm text-gray-500">Tipo:</span>
              <p className="font-medium capitalize">{data.businessType || '-'}</p>
            </div>
            <div>
              <span className="text-sm text-gray-500">Email:</span>
              <p className="font-medium">{data.email || '-'}</p>
            </div>
            <div>
              <span className="text-sm text-gray-500">Telefone:</span>
              <p className="font-medium">{data.phone || '-'}</p>
            </div>
            <div className="col-span-2">
              <span className="text-sm text-gray-500">Slogan:</span>
              <p className="font-medium">{data.slogan || 'Não definido'}</p>
            </div>
          </div>
        </div>

        {/* Domínio */}
        <div className="border border-gray-200 rounded-lg p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="bg-purple-100 p-2 rounded-lg">
              <Globe className="text-purple-600" size={24} />
            </div>
            <h4 className="font-semibold text-lg">Domínio</h4>
          </div>
          <div className="space-y-2">
            {data.domain ? (
              <>
                <div>
                  <span className="text-sm text-gray-500">Domínio Principal:</span>
                  <p className="font-medium text-lg text-blue-600">{data.domain}</p>
                  <p className="text-xs text-amber-600 mt-1">
                    ⚠️ Você receberá instruções para configurar o DNS
                  </p>
                </div>
                <div>
                  <span className="text-sm text-gray-500">Domínio de Teste (Staging):</span>
                  <p className="font-medium">{data.businessName ? `${data.businessName.toLowerCase().replace(/\s+/g, '-')}.pescar.app` : '-'}</p>
                  <p className="text-xs text-green-600 mt-1">
                    ✅ Disponível imediatamente após criação
                  </p>
                </div>
              </>
            ) : (
              <div>
                <span className="text-sm text-gray-500">Domínio:</span>
                <p className="font-medium text-lg text-blue-600">
                  {data.businessName ? `${data.businessName.toLowerCase().replace(/\s+/g, '-')}.pescar.app` : '-'}
                </p>
                <p className="text-xs text-green-600 mt-1">
                  ✅ Domínio gratuito .pescar.app
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Personalização */}
        <div className="border border-gray-200 rounded-lg p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="bg-pink-100 p-2 rounded-lg">
              <Palette className="text-pink-600" size={24} />
            </div>
            <h4 className="font-semibold text-lg">Personalização</h4>
          </div>
          <div className="grid grid-cols-3 gap-4">
            <div>
              <span className="text-sm text-gray-500">Template:</span>
              <p className="font-medium capitalize">{data.templateId?.replace(/-/g, ' ') || 'Pousada Clássica'}</p>
            </div>
            <div>
              <span className="text-sm text-gray-500">Cor Primária:</span>
              <div className="flex items-center gap-2 mt-1">
                <div 
                  className="w-6 h-6 rounded border"
                  style={{ backgroundColor: data.branding?.primaryColor || '#1e40af' }}
                />
                <span className="text-sm font-mono">{data.branding?.primaryColor || '#1e40af'}</span>
              </div>
            </div>
            <div>
              <span className="text-sm text-gray-500">Fonte:</span>
              <p className="font-medium" style={{ fontFamily: data.branding?.fontFamily || 'Inter' }}>
                {data.branding?.fontFamily || 'Inter'}
              </p>
            </div>
          </div>
        </div>

        {/* Módulos */}
        <div className="border border-gray-200 rounded-lg p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="bg-green-100 p-2 rounded-lg">
              <Settings className="text-green-600" size={24} />
            </div>
            <h4 className="font-semibold text-lg">Módulos Ativos</h4>
          </div>
          <div className="flex flex-wrap gap-2">
            {data.enabledModules && Object.entries(data.enabledModules).map(([key, enabled]) => 
              enabled && (
                <span key={key} className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm font-medium">
                  ✓ {key.charAt(0).toUpperCase() + key.slice(1)}
                </span>
              )
            )}
            <span className="text-sm text-gray-500 ml-2">
              ({enabledModulesCount} módulos ativos)
            </span>
          </div>
        </div>
      </div>

      <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-lg p-6">
        <h4 className="font-semibold text-green-900 mb-2">🎉 Próximos Passos:</h4>
        <ul className="space-y-2 text-sm text-green-800">
          <li>✅ Seu site será criado em segundos</li>
          <li>✅ Você receberá um email com instruções de acesso</li>
          <li>✅ Poderá começar a editar imediatamente</li>
          {data.domain && (
            <li>⚠️ Configurar DNS para ativar o domínio personalizado</li>
          )}
        </ul>
      </div>
    </div>
  );
};
